import * as THREE from 'three';
import { TileCoordinate, LODLevel, CubeFace } from '../Types';
import { CubeFaceGeometry } from '../CubeFaceGeometry';
import { RENDERING, LOADING } from './constants';
import { TextureCache } from './TextureCache';
import { SpatialIndex } from './SpatialIndex';
import { UrlBuilder } from './UrlBuilder';
import { waitFrames } from './utils';

/**
 * Events emitted by SceneAdapter
 */
export interface SceneAdapterEvents {
  meshAdded: (mesh: THREE.Mesh, coordinate: TileCoordinate) => void;
  meshRemoved: (mesh: THREE.Mesh, coordinate: TileCoordinate) => void;
  meshDisposed: (mesh: THREE.Mesh) => void;
}

/**
 * Manages THREE.js scene operations for tile meshes
 * - Ensures only one LOD is visible per spatial area (prevents z-fighting)
 * - Applies deterministic render order & polygon offset
 * - Uses SpatialIndex’s new shape (area → level → tileKey → mesh)
 */
export class SceneAdapter {
  private readonly scene: THREE.Scene;
  private readonly textureCache: TextureCache;
  private readonly spatialIndex: SpatialIndex;
  private lodLevels: LODLevel[]; // Made non-readonly to allow updates
  private readonly eventHandlers = new Map<keyof SceneAdapterEvents, Set<Function>>();

  constructor(
    scene: THREE.Scene,
    textureCache: TextureCache,
    spatialIndex: SpatialIndex,
    lodLevels: LODLevel[]
  ) {
    this.scene = scene;
    this.textureCache = textureCache;
    this.spatialIndex = spatialIndex;
    this.lodLevels = lodLevels;
  }

  /**
   * Create and add tile mesh to scene
   */
  async createTileMesh(
    coordinate: TileCoordinate,
    texture: THREE.Texture,
    textureUrl: string
  ): Promise<THREE.Mesh> {
    // Find tiles per face for this level
    const lodLevel = this.lodLevels.find((lod) => lod.level === coordinate.level);
    const tilesPerFace = lodLevel?.tilesPerFace || 1;

    // Create mesh using CubeFaceGeometry
    const mesh = CubeFaceGeometry.createTileMesh(coordinate, tilesPerFace, texture);

    // Order & polygon offset
    this.applyRenderOrdering(mesh, coordinate.level);

    // Metadata
    const tileKey = UrlBuilder.generateTileKey(coordinate);
    mesh.userData = mesh.userData || {};
    mesh.userData.tileKey = tileKey;
    mesh.userData.textureUrl = textureUrl;
    mesh.userData.isTile = true;
    mesh.userData.face = coordinate.face;
    mesh.userData.level = coordinate.level;
    mesh.userData.x = coordinate.x;
    mesh.userData.y = coordinate.y;

    // (1) keep it invisible until fully ready
    mesh.visible = false;

    // Refcount texture
    this.textureCache.incRef(textureUrl);

    // Add to index & scene
    this.spatialIndex.addTile(coordinate, mesh);
    this.scene.add(mesh);

    // (2) wait until texture is really usable on GPU
    await this.ensureTextureFullyLoaded(texture);
    await waitFrames(1);

    // (3) promote with a safe crossfade (no z-fighting)
    const spatialKey = UrlBuilder.generateSpatialKey(coordinate);
    await this.crossfadePromote(spatialKey, coordinate.level, mesh, 140);

    // Events
    this.emit('meshAdded', mesh, coordinate);
    return mesh;

  }

  /**
   * Remove tile mesh from scene with proper disposal
   */
  removeTileMesh(tileKey: string): boolean {
    const mesh = this.spatialIndex.getTile(tileKey);
    if (!mesh) return false;

    // Extract coordinate from mesh userData
    const coordinate: TileCoordinate = {
      x: mesh.userData.x,
      y: mesh.userData.y,
      face: mesh.userData.face,
      level: mesh.userData.level,
    };

    // Remove from scene
    this.scene.remove(mesh);

    // Remove from spatial index
    this.spatialIndex.removeTile(tileKey);

    // Dispose mesh resources + cache ref
    this.disposeMesh(mesh);

    // Emit events
    this.emit('meshRemoved', mesh, coordinate);
    this.emit('meshDisposed', mesh);

    return true;
  }

  /**
   * Safely dispose mesh geometry/material and release texture ref
   */
  disposeMesh(mesh: THREE.Mesh): void {
    // Dispose geometry
    mesh.geometry?.dispose?.();

    // Dispose materials (support multi-material)
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      // @ts-ignore map may exist on specific material types
      if (m?.map && typeof m.map.dispose === 'function') m.map.dispose();
      // @ts-ignore
      if (typeof m?.dispose === 'function') m.dispose();
    }

    // Release texture reference
    const textureUrl: string | undefined = mesh.userData?.textureUrl;
    if (textureUrl) {
      this.textureCache.decRef(textureUrl);
      mesh.userData.textureUrl = undefined; // prevent double release
    }
  }

  /**
   * Ensure texture is fully loaded and rendered before proceeding
   */
  async ensureTextureFullyLoaded(texture: THREE.Texture): Promise<void> {
    return new Promise<void>((resolve) => {
      // If image-backed texture still loading, wait
      // (Compressed/Video textures may not use image.complete; we still wait a frame below)
      if ((texture as any).image && (texture as any).image.complete === false) {
        const handleLoad = () => {
          waitFrames(2).then(() => resolve());
        };
        (texture as any).image.onload = handleLoad;
        (texture as any).image.onerror = handleLoad;
        setTimeout(handleLoad, 200); // fallback
      } else {
        waitFrames(1).then(() => resolve());
      }
    });
  }

  /**
   * Wait for mesh to be fully rendered
   */
  async waitForMeshRendering(mesh: THREE.Mesh): Promise<void> {
    mesh.updateMatrixWorld(true);
    await waitFrames(LOADING.MAX_RENDER_WAIT_FRAMES);
    if (!this.scene.children.includes(mesh)) {
      throw new Error('Mesh was removed from scene during rendering wait');
    }
  }

  /**
   * Hide meshes at lower levels in spatial area
   */
  hideLowerLevelMeshes(spatialKey: string, newLevel: number): void {
    this.spatialIndex.hideLowerLevelTilesInSpatialArea(spatialKey, newLevel);
  }

  /**
   * Show meshes in spatial area (compat: now shows the HIGHEST available LOD ONLY)
   * If you need a specific level visible, call spatialIndex.showOnlyLevel(spatialKey, level).
   */
  showMeshesInSpatialArea(spatialKey: string): void {
    const levels = this.spatialIndex.getSpatialTiles(spatialKey);
    if (!levels || levels.size === 0) return;

    let maxLevel: number | undefined;
    for (const l of levels.keys()) {
      if (maxLevel === undefined || l > maxLevel) maxLevel = l;
    }
    if (maxLevel !== undefined) {
      this.spatialIndex.showOnlyLevel(spatialKey, maxLevel, { requireVisibleTarget: true });
    }
  }

  /**
   * Get lower level meshes in spatial area
   */
  getLowerLevelMeshes(spatialKey: string, newLevel: number): THREE.Mesh[] {
    return this.spatialIndex.getLowerLevelTilesInSpatialArea(spatialKey, newLevel);
  }

  /**
   * Remove multiple meshes in batch
   */
  async removeMeshesBatch(tileKeys: string[], batchSize = 5, delayBetweenBatches = 0): Promise<void> {
    for (let i = 0; i < tileKeys.length; i += batchSize) {
      const batch = tileKeys.slice(i, i + batchSize);

      for (const tileKey of batch) {
        this.removeTileMesh(tileKey);
      }

      if (delayBetweenBatches > 0 && i + batchSize < tileKeys.length) {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenBatches));
      }
    }
  }

  /**
   * Get all tile meshes in scene
   */
  getAllTileMeshes(): THREE.Mesh[] {
    const tileMeshes: THREE.Mesh[] = [];
    for (const child of this.scene.children) {
      if (child instanceof THREE.Mesh && child.userData?.isTile) {
        tileMeshes.push(child);
      }
    }
    return tileMeshes;
  }

  /**
   * Get tile meshes for specific face
   */
  getTileMeshesForFace(face: string): THREE.Mesh[] {
    const faceMeshes: THREE.Mesh[] = [];
    for (const child of this.scene.children) {
      if (
        child instanceof THREE.Mesh &&
        child.userData?.isTile &&
        String(child.userData?.face) === face
      ) {
        faceMeshes.push(child);
      }
    }
    return faceMeshes;
  }

  /**
   * Get tile meshes for specific face and level
   */
  getTileMeshesForFaceAndLevel(face: string, level: number): THREE.Mesh[] {
    const meshes: THREE.Mesh[] = [];
    for (const child of this.scene.children) {
      if (
        child instanceof THREE.Mesh &&
        child.userData?.isTile &&
        String(child.userData?.face) === face &&
        child.userData?.level === level
      ) {
        meshes.push(child);
      }
    }
    return meshes;
  }

  /**
   * Force remove all level 1 tiles for a specific face
   */
  forceRemoveAllLevel1TilesForFace(face: string | CubeFace): void {
    const level1Meshes = this.getTileMeshesForFaceAndLevel(String(face), 1);
    for (const mesh of level1Meshes) {
      const tileKey = mesh.userData?.tileKey as string | undefined;
      if (tileKey) this.removeTileMesh(tileKey);
    }
    // Mark face as having level 1 tiles removed
    this.spatialIndex.markLevel1FaceRemoved(face as CubeFace);
  }

  /**
   * Count tile meshes by level
   */
  countTileMeshesByLevel(): Record<number, number> {
    const counts: Record<number, number> = {};
    for (const child of this.scene.children) {
      if (child instanceof THREE.Mesh && child.userData?.isTile) {
        const level = child.userData.level as number | undefined;
        if (level !== undefined) counts[level] = (counts[level] || 0) + 1;
      }
    }
    return counts;
  }

  /**
   * Count tile meshes by face
   */
  countTileMeshesByFace(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const child of this.scene.children) {
      if (child instanceof THREE.Mesh && child.userData?.isTile) {
        const face = String(child.userData.face ?? 'UNKNOWN');
        counts[face] = (counts[face] || 0) + 1;
      }
    }
    return counts;
  }

  /**
   * Validate scene consistency
   */
  validateScene(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    const tracked = this.spatialIndex.getVisibleTiles();
    const sceneTileMeshes = this.getAllTileMeshes();

    // A) All spatial index tiles must be in scene
    for (const [tileKey, mesh] of tracked) {
      if (!this.scene.children.includes(mesh)) {
        issues.push(`Tile ${tileKey} in spatial index but not in scene`);
      }
    }

    // B) All scene tile meshes must be in spatial index
    for (const mesh of sceneTileMeshes) {
      const tileKey = mesh.userData?.tileKey as string | undefined;
      if (!tileKey || !tracked.has(tileKey)) {
        issues.push(`Scene tile mesh without corresponding spatial index entry`);
      }
    }

    // C) Mesh metadata consistency
    for (const mesh of sceneTileMeshes) {
      const ud = mesh.userData || {};
      if (!ud.isTile) issues.push('Tile mesh missing isTile flag in userData');
      if (!ud.tileKey) issues.push('Tile mesh missing tileKey in userData');

      if (ud.tileKey) {
        const expectedKey = UrlBuilder.generateTileKey({
          x: ud.x,
          y: ud.y,
          face: ud.face,
          level: ud.level,
        });
        if (ud.tileKey !== expectedKey) {
          issues.push(`Tile mesh has incorrect tileKey: expected ${expectedKey}, got ${ud.tileKey}`);
        }
      }
    }

    return { isValid: issues.length === 0, issues };
  }

  /**
   * Clear all tile meshes from scene and index
   */
  clearAllTileMeshes(): void {
    const tileMeshes = this.getAllTileMeshes();
    for (const mesh of tileMeshes) {
      this.scene.remove(mesh);
      this.disposeMesh(mesh);
    }
    this.spatialIndex.clear();
  }

  /**
   * Add event listener
   */
  on<K extends keyof SceneAdapterEvents>(event: K, handler: SceneAdapterEvents[K]): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler as any);
  }

  /**
   * Remove event listener
   */
  off<K extends keyof SceneAdapterEvents>(event: K, handler: SceneAdapterEvents[K]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) handlers.delete(handler as any);
  }

  /**
   * Emit event
   */
  private emit<K extends keyof SceneAdapterEvents>(
    event: K,
    ...args: Parameters<SceneAdapterEvents[K]>
  ): void {
    const handlers = this.eventHandlers.get(event);
    if (!handlers) return;
    for (const handler of handlers) {
      try {
        (handler as any)(...args);
      } catch (error) {
        // Silently handle event errors
      }
    }
  }

  /**
   * Update LOD levels configuration
   * This allows dynamic updating of LOD levels based on metadata
   */
  updateLODLevels(lodLevels: LODLevel[]): void {
    if (!lodLevels || lodLevels.length === 0) {
      throw new Error('LOD levels cannot be empty');
    }
    this.lodLevels = [...lodLevels];
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    this.clearAllTileMeshes();
    this.eventHandlers.clear();
  }

  // ----------------- Internals -----------------

  private applyRenderOrdering(mesh: THREE.Mesh, level: number): void {
    mesh.renderOrder = (RENDERING.BASE_RENDER_ORDER ?? 1000) + level;

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (!m) continue;
      // @ts-ignore
      m.depthTest = true;
      // @ts-ignore
      m.depthWrite = true;
      // @ts-ignore
      m.polygonOffset = true;
      // @ts-ignore
      m.polygonOffsetFactor = RENDERING.POLYGON_OFFSET_FACTOR ?? -1;
      // @ts-ignore
      m.polygonOffsetUnits = (RENDERING.POLYGON_OFFSET_UNITS ?? -1) * level;
    }
  }

  private setOpacity(mesh: THREE.Mesh, opacity: number, transparent = true) {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (!m) continue;
      // @ts-ignore
      m.transparent = transparent;
      // @ts-ignore
      m.opacity = opacity;
    }
  }

  private setDepthWrite(mesh: THREE.Mesh, depthWrite: boolean) {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (!m) continue;
      // @ts-ignore
      m.depthWrite = depthWrite;
    }
  }

  private async crossfadePromote(
    spatialKey: string,
    targetLevel: number,
    newMesh: THREE.Mesh,
    durationMs = 160
  ): Promise<void> {
    // --- local helpers ---
    const mats = (mesh: THREE.Mesh) =>
      (Array.isArray(mesh.material) ? mesh.material : [mesh.material])
        .filter(Boolean) as THREE.Material[];
  
    const setOpacity = (mesh: THREE.Mesh, opacity: number, transparent = true) => {
      for (const m of mats(mesh)) {
        // @ts-ignore
        m.transparent = transparent;
        // @ts-ignore
        m.opacity = opacity;
        // crucial when toggling transparency at runtime
        // @ts-ignore
        m.needsUpdate = true;
      }
    };
  
    const setDepthWrite = (mesh: THREE.Mesh, depthWrite: boolean) => {
      for (const m of mats(mesh)) {
        // @ts-ignore
        m.depthWrite = depthWrite;
        // @ts-ignore
        m.depthTest = true;
        // @ts-ignore
        m.needsUpdate = true;
      }
    };
  
    const ease = (t: number) => t * t * (3 - 2 * t); // smoothstep
  
    // Compute expected children count for this promotion (usually 4 in quadtree)
    const expectedChildrenCount = (() => {
      const parentLevel = targetLevel - 1;
      const childLod = this.lodLevels.find(l => l.level === targetLevel);
      const parentLod = this.lodLevels.find(l => l.level === parentLevel);
      const childTPF = childLod?.tilesPerFace ?? 1;
      const parentTPF = parentLod?.tilesPerFace ?? 1;
      const factor = Math.max(1, Math.floor(childTPF / parentTPF));
      return factor * factor; // assume square split
    })();
  
    const areChildrenComplete = () => {
      const levels = this.spatialIndex.getSpatialTiles(spatialKey);
      const tileMap = levels?.get(targetLevel);
      return !!tileMap && tileMap.size >= expectedChildrenCount;
    };
  
    // --- Phase A: fade new child IN on top (parent stays visible) ---
    const originalRenderOrder = newMesh.renderOrder ?? 0;
    newMesh.visible = true;
    setOpacity(newMesh, 0, true);
    setDepthWrite(newMesh, false);           // avoid coplanar fighting during fade
    newMesh.renderOrder = originalRenderOrder + 0.5; // draw after parent while fading
  
    // Warm-up frames so GPU uploads/compilation settle (prevents momentary blacks)
    await waitFrames(2);
  
    // Fade IN new child (parent remains fully visible)
    await new Promise<void>((resolve) => {
      const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const step = (t: number) => {
        const now = (typeof performance !== 'undefined' ? t : Date.now());
        const k = ease(Math.min(1, (now - t0) / (durationMs * 0.6)));
        setOpacity(newMesh, k, true);
  
        if (!this.scene.children.includes(newMesh)) return resolve(); // safety
        if (k < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  
    // If not all children for this area/level are present yet,
    // STOP HERE: keep parent visible to avoid any holes.
    if (!areChildrenComplete()) {
      // keep child fully visible but depthWrite off to prevent z-fighting
      setOpacity(newMesh, 1, true);
      setDepthWrite(newMesh, false);
      // We'll complete the swap when the next child arrives and calls this again.
      return;
    }
  
    // --- Phase B: all children ready → fade OUT parent(s) as a group, then swap ---
    const parents = this.spatialIndex
      .getLowerLevelTilesInSpatialArea(spatialKey, targetLevel)
      .filter(m => m.visible); // only those currently covering
  
    // Make sure all children at this level are opaque/visible for a clean handoff
    {
      const levels = this.spatialIndex.getSpatialTiles(spatialKey);
      const children = levels?.get(targetLevel);
      if (children) {
        for (const [, childMesh] of children) {
          childMesh.visible = true;
          setOpacity(childMesh, 1, true);
          setDepthWrite(childMesh, false); // children on top during the crossfade
          childMesh.renderOrder = (childMesh.renderOrder ?? 0) + 0.5;
        }
      }
    }
  
    // Fade OUT parents
    await new Promise<void>((resolve) => {
      const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      for (const m of parents) setOpacity(m, 1, true);
  
      const step = (t: number) => {
        const now = (typeof performance !== 'undefined' ? t : Date.now());
        const k = ease(Math.min(1, (now - t0) / (durationMs * 0.4)));
        for (const m of parents) setOpacity(m, 1 - k, true);
  
        if (k < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });
  
    // Finalize: enforce single-LOD (AFTER child is visible); restore flags
    try {
      // @ts-ignore optional safe signature
      this.spatialIndex.showOnlyLevel(spatialKey, targetLevel, { requireVisibleTarget: true });
    } catch {
      this.spatialIndex.showOnlyLevel(spatialKey, targetLevel);
    }
  
    // Children: restore solid state
    {
      const levels = this.spatialIndex.getSpatialTiles(spatialKey);
      const children = levels?.get(targetLevel);
      if (children) {
        for (const [, childMesh] of children) {
          setOpacity(childMesh, 1, false);
          setDepthWrite(childMesh, true);
          childMesh.renderOrder = (childMesh.renderOrder ?? 0) - 0.5;
        }
      }
    }
  
    // Parents: hide and restore materials
    for (const m of parents) {
      m.visible = false;
      setOpacity(m, 1, false);
      setDepthWrite(m, true);
    }
  
    // Restore the specific newMesh flags too (already included above, but explicit doesn’t hurt)
    setOpacity(newMesh, 1, false);
    setDepthWrite(newMesh, true);
    newMesh.renderOrder = originalRenderOrder;
  }
  
  
}
